import {
  Button,
  Checkbox,
  Col,
  Form,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Typography,
} from 'antd';
import React, { useCallback, useEffect } from 'react';

import { CustomPricing, CustomPricingInput } from '../../models/CustomPricing';
import { Mode } from '../../models/Mode';

interface IProps {
  carrierId: number;
  modes: Mode[];
  configs: CustomPricing[];
  selectedConfig?: CustomPricing;
  submitting?: boolean;
  error: Error | null;
  onSubmit: (values: CustomPricingInput) => void;
  onClose: () => void;
}

const PricingModal: React.FC<IProps> = ({
  carrierId: _carrierId,
  configs,
  error,
  modes,
  onClose,
  onSubmit,
  selectedConfig,
  submitting,
}) => {
  const [form] = Form.useForm();

  const isEditing = !!selectedConfig;

  const handleSubmit = useCallback(
    (values: CustomPricingInput) => {
      onSubmit({
        ...values,
        allottedWaitTime: values.allottedWaitTime ?? 0,
        baseFee: values.baseFee ?? 0,
        minimumFee: values.minimumFee ?? 0,
        minimumIncludesBaseFee: values.minimumIncludesBaseFee ?? true,
      });
    },
    [onSubmit]
  );

  const availableModes = isEditing
    ? modes
    : modes.filter(
        (mode) => !configs.some((config) => config.modeId === mode.id)
      );

  const closeModal = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  useEffect(() => {
    if (selectedConfig) {
      form.setFieldsValue(selectedConfig);
    }
  }, [form, selectedConfig]);

  useEffect(() => {
    if (error) {
      notification.error({
        description: error.message,
        message: error.name,
      });
    }
  }, [error]);

  return (
    <Modal
      centered
      open
      footer={null}
      maskClosable={false}
      title={isEditing ? 'Edit Custom Pricing' : 'Add Custom Pricing'}
      width={520}
      onCancel={closeModal}
    >
      <Form
        form={form}
        initialValues={{ minimumIncludesBaseFee: true }}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label="Mode"
              name="modeId"
              rules={[{ message: 'required', required: true }]}
            >
              <Select
                disabled={isEditing}
                options={availableModes.map((mode) => ({
                  label: mode.name,
                  value: mode.id,
                }))}
                placeholder="Select a mode"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Base Fee" name="baseFee">
              <InputNumber
                controls={false}
                min={0}
                prefix="$"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Hourly Rate"
              name="hourlyRate"
              rules={[{ message: 'required', required: true }]}
            >
              <InputNumber
                controls={false}
                min={0.01}
                prefix="$"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Speed (km/h)"
              name="speed"
              rules={[{ message: 'required', required: true }]}
            >
              <InputNumber
                controls={false}
                min={0.01}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Allotted Wait Time (hrs)" name="allottedWaitTime">
              <InputNumber controls={false} min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Minimum Fee" name="minimumFee">
              <InputNumber
                controls={false}
                min={0}
                prefix="$"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="minimumIncludesBaseFee" valuePropName="checked">
              <Checkbox>Minimum Includes Base Fee</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Typography.Paragraph
          style={{
            fontSize: 12,
            marginBottom: 16,
            marginTop: -16,
            textAlign: 'justify',
          }}
          type="secondary"
        >
          When checked, the minimum fee affects the entire bill (base fee +
          travel cost). When unchecked, the base fee is always added on top of
          the travel cost or minimum fee, whichever is greater.
        </Typography.Paragraph>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item>
              <Button className="modal-close-btn" onClick={closeModal}>
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <Button
                className="modal-save-btn"
                htmlType="submit"
                loading={submitting}
                type="primary"
              >
                SAVE
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PricingModal;
